import {
  BubbleChartCommands,
  BubbleChartDomains,
  BubbleChartEmptyStateData,
  BubbleChartRenderingData,
  BubbleChartViewInterface,
  BubbleChartViewModelSourceData,
} from '../types';
import {
  DEFAULT_BUBBLE_CHART_VIEW_SETTINGS,
  DEFAULT_CHART_WINDOW_STATE,
  EMPTY_CLIENT_RECT,
  EMPTY_HIGHLIGHT_STATE,
} from '../consts';
import { returnNull } from '@ardoq/common-helpers';
import { DEFAULT_ARDOQ_I18NLOCALE } from '@ardoq/i18n';
import { ARDOQ_DEFAULT_LOCALE } from '@ardoq/locale';
import {
  DEFAULT_CHART_DIMENSIONS,
  DEFAULT_COMPONENT_LABEL_FILTER,
  DEFAULT_MARGIN,
} from '@ardoq/graph';
import { noop } from 'lodash';

const DEFAULT_BUBBLE_CHART_COMMANDS: BubbleChartCommands = {
  updateHoverState: noop,
  updateFocusState: noop,
  setCenter: noop,
  setScale: noop,
  panSettle: noop,
  selectComponent: noop,
  onBubbleClick: noop,
  onBubbleDoubleClick: noop,
};

export const INITIAL_VIEW_INTERFACE: BubbleChartViewInterface = {
  contextMenuName: null,
  onViewSettingsUpdate: noop,

  get: returnNull,
  getComponentDisplayName: returnNull,
  isComponentIncludedInContextByFilter: () => true,
  getComponentParentId: returnNull,
  getComponentWorkspaceId: returnNull,
  getRootComponents: () => [],
  getComponentType: returnNull,
  getReferenceTypeId: returnNull,
  getReferenceTypeById: returnNull,

  getComponentCssClassNames: returnNull,
  getComponentDisplayColor: () => ({}),

  getComponentDisplayLabel: returnNull,
  getComponentFields: () => [],
  getComponentFieldValue: returnNull,
  getReferenceRootWorkspaceId: returnNull,
  getReferenceFields: () => [],
  getReferenceFieldValue: returnNull,
  getFieldByName: returnNull,
  getFieldLabel: returnNull,
  getFieldsOfWorkspace: () => [],
  formatField: returnNull,
  getComponentLabelFilter: () => DEFAULT_COMPONENT_LABEL_FILTER,
  getComponentTypeName: returnNull,

  getComponentLevel: () => 1,
};

const INITIAL_EMPTY_STATE: BubbleChartEmptyStateData = {
  isViewEmpty: false,
  noNumericFields: false,
  hasActiveFilter: false,
  isPresentationMode: false,
  allDescendantsIncluded: false,
  hasFieldsSelected: false,
  selectedComponentHasNumericFields: false,
  isWorkspaceEmpty: false,
};
export const INITIAL_SOURCE_DATA_STATE: Omit<
  BubbleChartViewModelSourceData,
  'viewInstanceId' | 'viewInterface'
> = {
  viewSettings: DEFAULT_BUBBLE_CHART_VIEW_SETTINGS,
  isLoadingData: true,
  data: [],
  xAxisTitle: '',
  yAxisTitle: '',
  numericFields: [],
  allNumericFields: [],
  availableBubbleComponentFields: [],
  availableReferenceFields: [],
  availableReferencedComponentFields: [],
  availableIncomingReferenceTypeNames: [],
  availableOutgoingReferenceTypeNames: [],
  sources: null,
  emptyStateData: INITIAL_EMPTY_STATE,
  isScopeDiffMode: false,
  activeDiffMode: null,
  isPresentationMode: false,
  popovers: {
    disabledZoomToQuadrantPopover: {},
  },
  isFilteredByRadius: false,
  radiusFieldData: null,
  locale: ARDOQ_DEFAULT_LOCALE,
  i18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
  hasReferenceBubbleChartFeature: false,
};

const EMPTY_BUBBLE_CHART_DOMAIN = {
  x: [0, 0],
  y: [0, 0],
  radius: [0, 0],
  labeledBubblesRadius: [0, 0],
} satisfies BubbleChartDomains;

const EMPTY_CHART_ATTRIBUTES = {
  ...DEFAULT_CHART_WINDOW_STATE,
  clientRect: EMPTY_CLIENT_RECT,
  margin: DEFAULT_MARGIN,
  dimensions: DEFAULT_CHART_DIMENSIONS,
  domain: EMPTY_BUBBLE_CHART_DOMAIN,
};

export const INITIAL_RENDERING_DATA_STATE: Omit<
  BubbleChartRenderingData,
  'viewInstanceId'
> = {
  ...INITIAL_SOURCE_DATA_STATE,
  ...EMPTY_CHART_ATTRIBUTES,
  viewInterface: INITIAL_VIEW_INTERFACE,
  bubbleChartCommands: DEFAULT_BUBBLE_CHART_COMMANDS,
  hoverState: EMPTY_HIGHLIGHT_STATE,
  focusState: EMPTY_HIGHLIGHT_STATE,
  quadrantSettings: null,
  chartRenderingParameters: null,
  getBubbleChartContentContainer: returnNull,
  selectedQuadrantData: null,
  quadrantZoomState: {
    selectedQuadrant: null,
    quadrantWindowStates: {
      topLeft: DEFAULT_CHART_WINDOW_STATE,
      topRight: DEFAULT_CHART_WINDOW_STATE,
      bottomLeft: DEFAULT_CHART_WINDOW_STATE,
      bottomRight: DEFAULT_CHART_WINDOW_STATE,
    },
  },
};
